import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  AutoAwesome,
  ShieldOutlined,
  InsightsOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { loginUser, clearError } from '../../store/slices/authSlice';

const schema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().min(6, 'At least 6 characters').required('Password is required'),
});

type LoginFormData = yup.InferType<typeof schema>;

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'Salon CRM';
const BRAND_TAGLINE = import.meta.env.VITE_BRAND_TAGLINE || 'Run your salon with one calm dashboard';

const featurePoints = [
  { icon: <InsightsOutlined />, title: 'Live insights', body: 'Revenue, bookings, and staff performance at a glance.' },
  { icon: <StorefrontOutlined />, title: 'Everything in one place', body: 'Appointments, inventory, payroll, and campaigns.' },
  { icon: <ShieldOutlined />, title: 'Enterprise-grade security', body: 'Tenant-isolated data and role-based access.' },
];

const LoginForm: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useAppSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: LoginFormData) => {
    dispatch(clearError());
    dispatch(loginUser(data));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'grey.50',
      }}
    >
      {isDesktop && (
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            color: '#FFFFFF',
            p: 6,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
            overflow: 'hidden',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.15,
              background:
                'radial-gradient(circle at 20% 10%, #FFF 0, transparent 35%), radial-gradient(circle at 80% 80%, #FFF 0, transparent 40%)',
            }}
          />
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha('#fff', 0.18),
                display: 'grid',
                placeItems: 'center',
                backdropFilter: 'blur(6px)',
              }}
            >
              <AutoAwesome fontSize="small" sx={{ color: '#FFF' }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#FFF', fontWeight: 700 }}>
              {BRAND_NAME}
            </Typography>
          </Stack>

          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 460 }}>
            <Typography
              variant="h3"
              sx={{ color: '#FFF', fontWeight: 700, lineHeight: 1.15, mb: 2, letterSpacing: '-0.01em' }}
            >
              {BRAND_TAGLINE}
            </Typography>
            <Typography variant="body1" sx={{ color: alpha('#FFF', 0.88), mb: 4, fontSize: '1.05rem' }}>
              Sign in to your account to manage appointments, staff, and customers — from a single, clear workspace.
            </Typography>

            <Stack spacing={2.5}>
              {featurePoints.map((p) => (
                <Stack key={p.title} direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      bgcolor: alpha('#FFF', 0.15),
                      display: 'grid',
                      placeItems: 'center',
                      color: '#FFF',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    {p.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#FFF', fontWeight: 600 }}>
                      {p.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#FFF', 0.82) }}>
                      {p.body}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Typography variant="caption" sx={{ color: alpha('#FFF', 0.7), position: 'relative', zIndex: 1 }}>
            © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          flex: isDesktop ? 1 : 'unset',
          width: isDesktop ? undefined : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          {!isDesktop && (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#FFF',
                }}
              >
                <AutoAwesome fontSize="small" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {BRAND_NAME}
              </Typography>
            </Stack>
          )}

          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 1, letterSpacing: '-0.01em' }}
          >
            Welcome back
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
            Sign in to continue to your dashboard.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2.5}>
              <TextField
                {...register('email')}
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email?.message || ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  },
                  '& .MuiFormHelperText-root': { minHeight: 20, mx: 0.5 },
                }}
              />

              <TextField
                {...register('password')}
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message || ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  },
                  '& .MuiFormHelperText-root': { minHeight: 20, mx: 0.5 },
                }}
              />

              <Box sx={{ textAlign: 'right', mt: '-8px !important' }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{ fontSize: '0.85rem', color: 'primary.dark', fontWeight: 500 }}
                  onClick={(e) => e.preventDefault()}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.6,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.32)}` },
                }}
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          <Box sx={{ my: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              New to {BRAND_NAME}?
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          </Box>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
            }}
          >
            Start a free trial
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginForm;
